import { useEffect, useState } from "react";
import { Box, Button, List, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from "@mui/material";
import { Aperture, Broadcast, ClockCounterClockwise, Monitor, Play, Users } from "@phosphor-icons/react";
import { ConnectionDots, Dot, PageHeader, SectionLabel, StatusChip, VideoFrame } from "../ui";
import { tones } from "../theme";
import { useNavigate } from "react-router-dom";
import { http, DicomNode, StreamInfo } from "../api";
import { useSession } from "../AuthToken";
import { WhepPlayer } from "../WhepPlayer";

interface LiveSession {
  id: string;
  startedAt: string;
  endedAt?: string | null;
  orderId?: string;
  accession: string;
  patient: string;
  instrument: string;
  participants: { kind: string; label: string }[];
}

export default function Live() {
  const { token } = useSession();
  const [nodes, setNodes] = useState<DicomNode[]>([]);
  const [sel, setSel] = useState<DicomNode | null>(null);
  const [stream, setStream] = useState<StreamInfo | null>(null);
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [recent, setRecent] = useState<LiveSession[]>([]);
  const [streamError, setStreamError] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!token) return;
    const load = () => {
      http.get<LiveSession[]>(token, "/sessions?status=live").then((r) => r.ok && setSessions(r.result));
      http.get<LiveSession[]>(token, "/sessions?status=ended").then((r) => r.ok && setRecent(r.result.slice(0, 15)));
    };
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [token]);
  useEffect(() => {
    if (token) http.get<DicomNode[]>(token, "/nodes").then((r) => r.ok && setNodes(r.result.filter((n) => n.live)));
  }, [token]);
  const watch = async (n: DicomNode) => {
    setSel(n);
    setStream(null);
    setStreamError("");
    const r = await http.get<StreamInfo>(token, `/nodes/${n.id}/stream`);
    if (r.ok) setStream(r.result);
    else setStreamError(r.error);
  };
  return (
    <Stack spacing={3}>
      <PageHeader overline="Remote care" title="Sessions" />
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        <Stack spacing={3} sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0 }}>
          <Box>
            <SectionLabel icon={<Broadcast size={16} />} count={sessions.length}>
              In progress
            </SectionLabel>
            <Paper sx={{ mt: 1.5 }}>
              <List dense sx={{ py: 0.75 }}>
                {sessions.map((s) => (
                  <ListItemButton key={s.id} onClick={() => navigate(`/sessions/${s.id}`)} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Dot color={tones.live} pulse />
                    </ListItemIcon>
                    <ListItemText
                      primary={s.patient}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondary={`${s.instrument} · ${s.accession} · since ${new Date(
                        s.startedAt
                      ).toLocaleTimeString()}`}
                    />
                    <StatusChip
                      status="live"
                      tone="live"
                      pulse={false}
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                          <Users size={12} />
                          <span>{s.participants.length} in call</span>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                ))}
                {sessions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                    No sessions in progress. To start one, open the order and select Start session.
                  </Typography>
                )}
              </List>
            </Paper>
          </Box>
          <Box>
            <SectionLabel icon={<ClockCounterClockwise size={16} />} count={recent.length}>
              Recent
            </SectionLabel>
            <Paper sx={{ mt: 1.5 }}>
              <List dense sx={{ py: 0.75 }}>
                {recent.map((s) => {
                  const mins = s.endedAt
                    ? Math.max(1, Math.round((new Date(s.endedAt).getTime() - new Date(s.startedAt).getTime()) / 60000))
                    : null;
                  return (
                    <ListItemButton
                      key={s.id}
                      onClick={() => navigate(s.orderId ? `/orders/${s.orderId}` : `/sessions/${s.id}`)}
                      sx={{ py: 1 }}
                    >
                      <ListItemText
                        primary={s.patient}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondary={`${new Date(s.startedAt).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}${mins ? ` · ${mins} min` : ""} · ${s.instrument}`}
                      />
                      {/* The ended session's page plays its recording, or says it is still being prepared */}
                      <Button
                        size="small"
                        startIcon={<Play size={14} weight="fill" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/sessions/${s.id}`);
                        }}
                        sx={{ flexShrink: 0, ml: 1 }}
                      >
                        Review recording
                      </Button>
                    </ListItemButton>
                  );
                })}
                {recent.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                    No completed sessions.
                  </Typography>
                )}
              </List>
            </Paper>
          </Box>
          <Box>
            <SectionLabel icon={<Aperture size={16} />} count={nodes.length}>
              Instrument preview
            </SectionLabel>
            <Paper sx={{ mt: 1.5 }}>
              <List dense sx={{ py: 0.75 }}>
                {nodes.map((n) => (
                  <ListItemButton key={n.id} selected={sel?.id === n.id} onClick={() => watch(n)} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: sel?.id === n.id ? "primary.main" : "text.secondary" }}>
                      <Aperture size={20} />
                    </ListItemIcon>
                    <ListItemText
                      primary={n.displayName ?? n.aet}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondary={`${n.device.tenant.code} / ${n.device.siteCode} / ${n.aet}`}
                    />
                  </ListItemButton>
                ))}
                {nodes.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                    No instrument in this practice provides live video.
                  </Typography>
                )}
              </List>
            </Paper>
          </Box>
        </Stack>
        <Stack sx={{ flexGrow: 1, width: "100%", minWidth: 0 }} spacing={1.5}>
          {sel && stream ? (
            <>
              <SectionLabel icon={<Monitor size={16} />}>{sel.displayName ?? sel.aet}</SectionLabel>
              <WhepPlayer key={stream.whepUrl} stream={stream} token={token} />
            </>
          ) : (
            <VideoFrame aspect="16 / 9">
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ position: "absolute", inset: 0, px: 2, textAlign: "center" }}
              >
                <ConnectionDots
                  state={!sel ? "idle" : streamError ? "failed" : "connecting"}
                  from={<Aperture size={22} />}
                  to={<Monitor size={22} />}
                  dots={9}
                  size={5}
                />
                <Typography color="text.secondary" variant="body2">
                  {!sel
                    ? "To view a live feed, select an instrument."
                    : streamError
                    ? `No live feed: ${streamError}`
                    : "Requesting the live feed…"}
                </Typography>
              </Stack>
            </VideoFrame>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
